// ---------------------------------------------------------------------------
// Procedural weapon inspect animations
// Sequences cycle each F press: flip → checkChamber → spin → admire → repeat
// Returns position + rotation offsets to apply to viewmodel transform.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnimationOffset {
  rotationX: number;
  rotationY: number;
  rotationZ: number;
  positionX: number;
  positionY: number;
  positionZ: number;
}

export const enum InspectSequence {
  Flip = 0,
  CheckChamber = 1,
  Spin = 2,
  Admire = 3,
}

const SEQUENCE_COUNT = 4;

/** Duration of each animation sequence in seconds. */
const SEQUENCE_DURATIONS: Record<InspectSequence, number> = {
  [InspectSequence.Flip]: 1.5,
  [InspectSequence.CheckChamber]: 1.2,
  [InspectSequence.Spin]: 1.0,
  [InspectSequence.Admire]: 2.0,
};

// ---------------------------------------------------------------------------
// Easing functions
// ---------------------------------------------------------------------------

/** Smooth ease-in-out cubic: slow start + slow end. */
export function easeInOutCubic(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Bounce-out easing. */
export function easeOutBounce(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const n1 = 7.5625;
  const d1 = 2.75;
  if (t < 1 / d1) {
    return n1 * t * t;
  } else if (t < 2 / d1) {
    const t2 = t - 1.5 / d1;
    return n1 * t2 * t2 + 0.75;
  } else if (t < 2.5 / d1) {
    const t2 = t - 2.25 / d1;
    return n1 * t2 * t2 + 0.9375;
  } else {
    const t2 = t - 2.625 / d1;
    return n1 * t2 * t2 + 0.984375;
  }
}

// ---------------------------------------------------------------------------
// Zero offset constant
// ---------------------------------------------------------------------------

const ZERO_OFFSET: AnimationOffset = Object.freeze({
  rotationX: 0,
  rotationY: 0,
  rotationZ: 0,
  positionX: 0,
  positionY: 0,
  positionZ: 0,
});

// ---------------------------------------------------------------------------
// Per-sequence offset calculators
// ---------------------------------------------------------------------------

/**
 * Flip: weapon rotates 360° around X axis over 1.5s with ease-in-out.
 * At t=0 and t=1.5 the rotation is 0 (full circle).
 */
function computeFlipOffset(time: number): AnimationOffset {
  const duration = SEQUENCE_DURATIONS[InspectSequence.Flip];
  const t = Math.min(time / duration, 1);
  const eased = easeInOutCubic(t);
  // Full 360° = 2*PI
  const rotX = eased * Math.PI * 2;
  return {
    rotationX: rotX % (Math.PI * 2) < 0.001 && t >= 1 ? 0 : rotX,
    rotationY: 0,
    rotationZ: 0,
    positionX: 0,
    // Slight lift during flip for visual flair
    positionY: Math.sin(eased * Math.PI) * 0.03,
    positionZ: 0,
  };
}

/**
 * Check Chamber: pull back slide/bolt 30° over phase 1, pause, release.
 * Phase 1: 0–0.4s → pull back (rotate X -30°, shift Z back)
 * Phase 2: 0.4–0.7s → hold
 * Phase 3: 0.7–1.2s → snap forward (bounce)
 */
function computeCheckChamberOffset(time: number): AnimationOffset {
  const duration = SEQUENCE_DURATIONS[InspectSequence.CheckChamber];
  const pullEnd = 0.4;
  const holdEnd = 0.7;
  const releaseEnd = duration;

  const pullAngle = -Math.PI / 6; // -30°
  const pullZ = 0.04; // slide back offset

  if (time >= releaseEnd) {
    return { ...ZERO_OFFSET };
  }

  let rotX: number;
  let posZ: number;

  if (time <= pullEnd) {
    // Pull back
    const t = time / pullEnd;
    const eased = easeInOutCubic(t);
    rotX = pullAngle * eased;
    posZ = pullZ * eased;
  } else if (time <= holdEnd) {
    // Hold
    rotX = pullAngle;
    posZ = pullZ;
  } else {
    // Release with bounce
    const t = (time - holdEnd) / (releaseEnd - holdEnd);
    const eased = easeOutBounce(t);
    rotX = pullAngle * (1 - eased);
    posZ = pullZ * (1 - eased);
  }

  return {
    rotationX: rotX,
    rotationY: 0,
    rotationZ: 0,
    positionX: 0,
    positionY: 0,
    positionZ: posZ,
  };
}

/**
 * Spin: weapon spins 360° around Y axis over 1.0s.
 */
function computeSpinOffset(time: number): AnimationOffset {
  const duration = SEQUENCE_DURATIONS[InspectSequence.Spin];
  const t = Math.min(time / duration, 1);
  const eased = easeInOutCubic(t);
  const rotY = eased * Math.PI * 2;
  return {
    rotationX: 0,
    rotationY: rotY % (Math.PI * 2) < 0.001 && t >= 1 ? 0 : rotY,
    rotationZ: 0,
    positionX: 0,
    positionY: 0,
    positionZ: 0,
  };
}

/**
 * Admire: slow tilt left 15°, pause, tilt right 15°, return center.
 * Phase 1: 0–0.5s → tilt left (rotZ = +15°)
 * Phase 2: 0.5–0.7s → hold
 * Phase 3: 0.7–1.3s → tilt right through center to -15°
 * Phase 4: 1.3–1.5s → hold right
 * Phase 5: 1.5–2.0s → return center
 */
function computeAdmireOffset(time: number): AnimationOffset {
  const tiltAngle = (15 * Math.PI) / 180; // 15°

  let rotZ: number;

  if (time >= 2.0) {
    rotZ = 0;
  } else if (time <= 0.5) {
    // Tilt left
    const t = time / 0.5;
    rotZ = tiltAngle * easeInOutCubic(t);
  } else if (time <= 0.7) {
    // Hold left
    rotZ = tiltAngle;
  } else if (time <= 1.3) {
    // Sweep from +tilt to -tilt
    const t = (time - 0.7) / 0.6;
    const eased = easeInOutCubic(t);
    rotZ = tiltAngle * (1 - 2 * eased);
  } else if (time <= 1.5) {
    // Hold right
    rotZ = -tiltAngle;
  } else {
    // Return center
    const t = (time - 1.5) / 0.5;
    rotZ = -tiltAngle * (1 - easeInOutCubic(t));
  }

  return {
    rotationX: 0,
    rotationY: 0,
    rotationZ: rotZ,
    positionX: 0,
    // Subtle sway offset to sell the admire look
    positionY: Math.sin((time / 2.0) * Math.PI) * 0.01,
    positionZ: 0,
  };
}

// ---------------------------------------------------------------------------
// Sequence dispatch
// ---------------------------------------------------------------------------

type OffsetComputer = (time: number) => AnimationOffset;

const SEQUENCE_COMPUTERS: Record<InspectSequence, OffsetComputer> = {
  [InspectSequence.Flip]: computeFlipOffset,
  [InspectSequence.CheckChamber]: computeCheckChamberOffset,
  [InspectSequence.Spin]: computeSpinOffset,
  [InspectSequence.Admire]: computeAdmireOffset,
};

// ---------------------------------------------------------------------------
// InspectAnimationSystem
// ---------------------------------------------------------------------------

export class InspectAnimationSystem {
  private playing = false;
  private sequenceIndex = 0; // next sequence to play
  private activeSequence: InspectSequence = InspectSequence.Flip;
  private startTime = 0;
  private elapsed = 0;

  /** Start (or restart) the next inspect animation in the cycle. */
  play(): void {
    if (this.playing) {
      // Restart current animation
      this.elapsed = 0;
      return;
    }
    this.activeSequence = this.sequenceIndex as InspectSequence;
    this.sequenceIndex = (this.sequenceIndex + 1) % SEQUENCE_COUNT;
    this.playing = true;
    this.elapsed = 0;
  }

  /** Cancel the current animation immediately. */
  cancel(): void {
    this.playing = false;
    this.elapsed = 0;
  }

  /** Whether an animation is currently in progress. */
  isPlaying(): boolean {
    return this.playing;
  }

  /** The currently active (or last started) sequence. */
  currentSequence(): InspectSequence {
    return this.activeSequence;
  }

  // -- Auto-cancel events --

  onFire(): void {
    this.cancel();
  }

  onReload(): void {
    this.cancel();
  }

  onWeaponSwitch(): void {
    this.cancel();
  }

  onScope(): void {
    this.cancel();
  }

  /**
   * Compute the animation offset for the given elapsed time (in seconds since play()).
   * Once the animation completes, returns zero offsets and marks itself as not playing.
   */
  getAnimationOffset(time: number): AnimationOffset {
    if (!this.playing) {
      return { ...ZERO_OFFSET };
    }

    const duration = SEQUENCE_DURATIONS[this.activeSequence];
    if (time >= duration) {
      // Animation complete
      this.playing = false;
      this.elapsed = 0;
      return { ...ZERO_OFFSET };
    }

    this.elapsed = time;
    return SEQUENCE_COMPUTERS[this.activeSequence](time);
  }
}
